import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { NEVER, Observable, ReplaySubject, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';

import {
    ApiThesaurusConcept,
    BaseContainer,
    BaseNestedFieldDefinition,
    FieldType,
    isNotNullOrUndefined,
    QueryPart,
    QueryPartsToStructuredQuery,
    ThesaurusConceptReference,
    UrlQueryParams,
    UrlQueryParamsConverter
} from '@stylehaven/components';
import { MHSearchRoutingService } from '../../../common/services/common/search-routing.service';
import { ThesaurusSandbox } from '../../sandboxes/thesaurus.sandbox.service';

@Component({
    selector: 'med-thesaurus-concept',
    templateUrl: './thesaurus-concept.component.html',
    styleUrls: ['./thesaurus-concept.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThesaurusConceptContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    @Input() set conceptReference(ref: ThesaurusConceptReference) {
        this.initConcept(ref);
    }

    @Input() thesaurusFieldDefinition: BaseNestedFieldDefinition;

    public label: string;
    public conceptUri$: ReplaySubject<string> = new ReplaySubject<string>(1);
    public showConceptDetails$: Subject<Event> = new Subject<Event>();

    private conceptDetails$: Observable<ApiThesaurusConcept>;

    constructor(
        private thesaurusSandbox: ThesaurusSandbox,
        private searchRoutingService: MHSearchRoutingService
    ) {
        super();
    }

    ngOnInit(): void {
        this.conceptDetails$ = this.conceptUri$.pipe(
            map(uri => uri.split('/').pop()),
            isNotNullOrUndefined(),
            switchMap(
                (id: string) =>
                    this.thesaurusSandbox
                        .getThesaurusConcept(this.thesaurusFieldDefinition.DottedKey, id)
                        .pipe(catchError(err => NEVER)) // necessary so shareReplay doesn't resubscribe
                // TODO: provide better user-feedback
            ),
            shareReplay(1)
        );

        this.showConceptDetails$
            .pipe(
                filter(event => !!event),
                throttleTime(200),
                switchMap(() => this.conceptDetails$.pipe(take(1))),
                // withLatestFrom would actively subscribe, and execute the http call too soon.
                takeUntil(this.destroy$)
            )
            .subscribe(thesaurusConcept => this.openConceptDialog(thesaurusConcept));
    }

    private initConcept(conceptReference: ThesaurusConceptReference): void {
        this.label = this.thesaurusSandbox.getThesaurusConceptReferenceLabel(conceptReference);
        this.conceptUri$.next(conceptReference.Uri);
    }

    public showConceptDetails(event: Event): void {
        this.showConceptDetails$.next(event);
    }

    private openConceptDialog(thesaurusConcept: ApiThesaurusConcept): void {
        this.thesaurusSandbox.openThesaurusConceptDialog(thesaurusConcept, this.thesaurusFieldDefinition, this.label);
    }

    get routerLink(): string[] {
        return this.searchRoutingService.getSearchRouterLink();
    }

    getSearchRouterParams(uriFieldDefinition: BaseNestedFieldDefinition, uri: string): UrlQueryParams {
        return UrlQueryParamsConverter.generateQueryParams(
            QueryPartsToStructuredQuery([new QueryPart({ field: uriFieldDefinition.DottedKey, values: [uri] })])
        );
    }

    getSearchState(uriFieldDefinition: BaseNestedFieldDefinition, uri: string): { [k: string]: any } {
        return this.searchRoutingService.getTermRouterLinkState(uriFieldDefinition.DottedKey, uri);
    }

    get uriFieldDefinition(): BaseNestedFieldDefinition | undefined {
        return this.thesaurusFieldDefinition.Children?.find(child => child.Type === FieldType.ThesaurusUriField) as
            | BaseNestedFieldDefinition
            | undefined;
    }

    ngOnDestroy(): void {
        this.conceptUri$.complete();

        this.showConceptDetails$.complete();

        super.ngOnDestroy();
    }
}
